import React, { useState, useRef, useEffect } from 'react';
import api from '@src/../js/api';

interface Variant {
  id: string;
  title: string;
  subtext?: string;
  price?: number;
  available: boolean;
}

interface TagOption {
  id: string;
  title: string;
  subtext?: string;
  price?: null;
  available: boolean;
  isTag: boolean;
  productId?: string;
  productHandle?: string;
  additionalDetails?: string; // For tag-based options, additional details can be shown
}

interface Product {
  id: string;
  handle?: string;
  variants?: Variant[];
  tags?: string[];
}

interface TagProduct {
  id: string;
  title: string;
  handle: string;
  tags: string[];
  variants: {
    edges: Array<{
      node: {
        id: string;
        availableForSale: boolean;
        price: {
          amount: string;
        };
      };
    }>;
  };
}

interface ComponentProps {
  block_id?: string;
  block_settings?: any;
  product?: Product;
  product_form_id?: string;
  [key: string]: any;
}

/**
 * ProductVariantSelector Component
 *
 * This component provides a dropdown selector for product variants or tag-based product options.
 *
 * Features:
 * - Regular variant selection from product.variants
 * - Tag-based product selection using GraphQL to fetch products with matching tags
 * - Two action modes for tag selection:
 *   - "redirect": Navigate to the selected product page
 *   - "variant_change": Update the current form with the selected product's variant ID
 * - Customizable styling and responsive design
 * - Loading states and error handling
 * - Accessibility support
 *
 * Usage:
 * - Set variant_source to "variants" for regular variant selection
 * - Set variant_source to "tags" and provide tag_name to fetch products by tag
 * - Configure tag_selection_action to control behavior when tag options are selected
 */

export const ProductVariantSelector = (props: ComponentProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<Variant | TagOption | null>(null);
  const [tagProducts, setTagProducts] = useState<TagProduct[]>([]);
  const [isLoadingTagProducts, setIsLoadingTagProducts] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Extract settings from block_settings with defaults
  const settings = props.block_settings || {};
  const {
    show_label = true,
    label_text = 'Select variant',
    placeholder_text = 'Choose an option',
    unavailable_text = 'Sold out',
    show_price_subtext = true,
    variant_source = 'variants',
    tag_name = '',
    field_to_display = 'title',
    tag_prefix_to_display = '',
    // Action settings
    tag_selection_action = 'redirect', // "redirect" or "variant_change"
    // Styling settings
    max_width = 400,
    label_font_size = 16,
    text_font_size = 16,
    subtext_font_size = 14,
    mobile_text_font_size = 14,
    mobile_subtext_font_size = 12,
    padding_vertical = 12,
    padding_horizontal = 16,
    option_padding_vertical = 12,
    mobile_padding_vertical = 10,
    mobile_padding_horizontal = 12,
    border_radius = 6,
    border_width = 1,
    // Colors
    label_color = '#000000',
    text_color = '#000000',
    subtext_color = '#666666',
    background_color = '#ffffff',
    background_hover_color = '#f8f8f8',
    border_color = '#c2c2c2',
    border_hover_color = '#9ca3af',
    border_active_color = '#3b82f6',
    focus_color = '#3b82f6',
    dropdown_background_color = '#ffffff',
    option_border_color = '#f3f4f6',
    option_hover_color = '#f8f9fa',
    option_selected_color = '#e0f2fe',
    unavailable_color = '#9ca3af'
  } = props.settings.block_settings;

  props = props.settings;
  const product = props.product;
  const product_form_id = props.product_form_id;
  const block_id = props.block_id;

  const ai_gen_id = block_id?.replace(/_/g, '').toLowerCase() || 'variant-selector';

  // Function to fetch products by tag using GraphQL
  const fetchProductsByTag = async (tagFilter: string): Promise<TagProduct[]> => {
    if (!tagFilter) return [];

    setIsLoadingTagProducts(true);

    const endpoint = '/api/2024-01/graphql.json';
    // Get the access token from window or use a default
    const accessToken =
      (window as any)?.Shopify?.theme?.storefront_access_token ||
      (window as any)?.STOREFRONT_ACCESS_TOKEN ||
      api.getStorefrontToken() ||
      null;

    if (!accessToken) {
      console.error(
        'No Shopify Storefront access token found. Make sure the token is available in window.Shopify.theme.storefront_access_token'
      );
      setIsLoadingTagProducts(false);
      return [];
    }

    // Create search query - handle both exact tag matches and prefix matches
    // For exact matches, use the full tag value, for prefix searches use wildcard
    const isExactTag = tagFilter.includes(':');

    if (!isExactTag) {
      setIsLoadingTagProducts(false);
      return [];
    }

    const searchQuery = `tag:"${tagFilter}"`;

    const query = `
      query GetProductsByTag(
        $query: String!
        $first: Int!
      ) {
        search(
          query: $query
          first: $first
          types: [PRODUCT]
        ) {
          edges {
            node {
              ... on Product {
                id
                title
                handle
                tags
                availableForSale
                variants(first: 5) {
                  edges {
                    node {
                      id
                      availableForSale
                      price {
                        amount
                        currencyCode
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    const variables = {
      query: searchQuery,
      first: 10
    };

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': accessToken
        },
        body: JSON.stringify({
          query: query,
          variables: variables
        })
      });

      if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.errors) {
        console.error('GraphQL errors:', result.errors);
        setIsLoadingTagProducts(false);
        return [];
      }

      const products = result?.data?.search?.edges?.map((edge: any) => edge.node) || [];

      // Filter products that actually contain the tag we're looking for
      const filteredProducts = products.filter((product: TagProduct) => {
        if (isExactTag) {
          // For exact tag matches, look for the exact tag value
          return product.tags.some((tag) => tag.toLowerCase() === tagFilter.toLowerCase());
        } else {
          // For prefix matches, look for tags that start with the prefix
          return product.tags.some((tag) => tag.toLowerCase().includes(tagFilter.toLowerCase()));
        }
      });

      setIsLoadingTagProducts(false);
      return filteredProducts;
    } catch (error) {
      console.error('Error fetching products by tag:', error);
      setIsLoadingTagProducts(false);
      return [];
    }
  };

  // Load tag products when variant_source is "tags" and tag_name is provided
  useEffect(() => {
    if (variant_source === 'tags' && tag_name && product?.tags) {
      // Find the actual tag value from the current product's tags
      const currentProductTag = product.tags.find((tag) => tag.toLowerCase().startsWith(tag_name.toLowerCase()));

      if (currentProductTag) {
        fetchProductsByTag(currentProductTag).then(setTagProducts);
      } else {
        // Fallback to the original tag_name if no matching tag found
        fetchProductsByTag(tag_name).then(setTagProducts);
      }
    }
  }, [variant_source, tag_name, product?.tags]);

  // Generate options based on variant_source
  const getOptions = (): (Variant | TagOption)[] => {
    if (variant_source === 'tags') {
      // Check if the current product has the required tag prefix
      if (tag_prefix_to_display && product?.tags) {
        const hasRequiredTag = product.tags.some((tag) =>
          tag.toLowerCase().startsWith(`${tag_prefix_to_display.toLowerCase()}`)
        );

        if (!hasRequiredTag) {
          return [];
        }
      }

      if (tagProducts.length === 0) {
        // If no tag products found, fall back to regular variants if available
        if (product?.variants && product.variants.length > 1) {
          return product.variants;
        }

        return [];
      }

      // Convert tag products to options
      const tagOptions: TagOption[] = tagProducts.map((tagProduct) => {
        // Extract the variant info for pricing/availability
        const firstVariant = tagProduct.variants?.edges?.[0]?.node;
        const isAvailable = tagProduct.availableForSale && (firstVariant?.availableForSale || false);

        // Create a clean title by finding and processing the matching tag
        let cleanTitle = tagProduct.title;
        let addtionalDetails = '';

        if (field_to_display === 'first_variant_title') {
          cleanTitle = firstVariant?.title || '';
        } else if (field_to_display === 'tag') {
          const tags = tagProduct.tags || [];
          cleanTitle =
            tags
              .find((tag) => tag.toLowerCase().startsWith(`${tag_prefix_to_display.toLowerCase()}:`))
              ?.split(':')?.[1] || '';

          if (cleanTitle.includes('_')) {
            const parts = cleanTitle.split('_');
            cleanTitle = parts[0].trim();
            addtionalDetails = parts[1]?.trim();
          }
        }

        if (!cleanTitle) return null;

        return {
          id: tagProduct.id,
          title: `${cleanTitle}`,
          subtext: ``, // Show full product title as subtext
          price: null, // We don't show price for tag-based options in the dropdown
          available: isAvailable,
          isTag: true,
          productId: tagProduct.id,
          productHandle: tagProduct.handle,
          additionalDetails: addtionalDetails || ''
        };
      });

      // Sort by availability (available first) then by title
      return tagOptions
        .filter((option) => option)
        .sort((a, b) => {
          const extractNumber = (val: string | undefined | null): number | null => {
            const match = val?.trim().match(/^(\d+(?:\.\d+)?)/); // extract leading number
            return match ? parseFloat(match[1]) : null;
          };

          const aVal = a.additionalDetails ?? '';
          const bVal = b.additionalDetails ?? '';

          const aNum = extractNumber(aVal);
          const bNum = extractNumber(bVal);

          if (aNum !== null && bNum !== null) {
            return aNum - bNum;
          } else if (aNum !== null) {
            return -1;
          } else if (bNum !== null) {
            return 1;
          } else {
            return aVal.localeCompare(bVal);
          }
        });
    }
    // Default to variants
    return product?.variants || [];
  };

  const options = getOptions();

  // Set initial selected option
  useEffect(() => {
    if (options.length > 0) {
      if (variant_source === 'tags' && product?.handle) {
        // For tag-based variants, find the option that matches the current product handle
        const currentProductOption = options.find(
          (option) => 'productHandle' in option && option.productHandle === product.handle
        );

        if (currentProductOption && (!selectedOption || selectedOption.id !== currentProductOption.id)) {
          setSelectedOption(currentProductOption);
          return;
        }
      }

      // Fallback: select first available option if no current product match or for regular variants
      if (!selectedOption) {
        const firstAvailable = options.find((option) => option.available);
        setSelectedOption(firstAvailable || options[0]);
      }
    }
  }, [options, variant_source, product?.handle]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleSelectOption = (option: Variant | TagOption) => {
    setSelectedOption(option);
    setIsOpen(false);

    // Handle tag-based selections
    if ('isTag' in option && option.isTag) {
      if (tag_selection_action === 'redirect' && option.productHandle) {
        // Redirect to the product page
        window.location.href = `/products/${option.productHandle}`;
        return;
      } else if (tag_selection_action === 'variant_change' && option.productId) {
        // Find the corresponding product in tagProducts and get its first variant
        const tagProduct = tagProducts.find((p) => p.id === option.productId);
        if (tagProduct?.variants?.edges?.[0]?.node?.id) {
          const variantId = tagProduct.variants.edges[0].node.id;

          // Update hidden select with the new variant ID
          if (product_form_id) {
            const hiddenSelect = document.querySelector(`#variant-select-${ai_gen_id}`) as HTMLSelectElement;
            if (hiddenSelect) {
              // Create option if it doesn't exist
              let optionElement = hiddenSelect.querySelector(`option[value="${variantId}"]`) as HTMLOptionElement;
              if (!optionElement) {
                optionElement = document.createElement('option');
                optionElement.value = variantId;
                optionElement.textContent = option.title;
                hiddenSelect.appendChild(optionElement);
              }

              hiddenSelect.value = variantId;
              hiddenSelect.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        }
        return;
      }
    }

    // Handle regular variant selections
    if (!('isTag' in option) && product_form_id) {
      const hiddenSelect = document.querySelector(`#variant-select-${ai_gen_id}`) as HTMLSelectElement;
      if (hiddenSelect) {
        hiddenSelect.value = option.id;
        hiddenSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggleDropdown();
    } else if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const formatPrice = (price?: number | null): string => {
    if (!price) return '';
    // Simple price formatting - in a real implementation, use Shopify's money filter
    return `$${(price / 100).toFixed(2)}`;
  };

  const styles = {
    container: {
      position: 'relative' as const,
      width: '100%',
      ...(max_width > 0 ? { maxWidth: `${max_width}px` } : {})
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      fontSize: `${label_font_size}px`,
      fontWeight: '600',
      color: label_color
    },
    dropdown: {
      position: 'relative' as const,
      width: '100%'
    },
    trigger: {
      width: '100%',
      padding: `${padding_vertical}px ${padding_horizontal}px`,
      backgroundColor: background_color,
      border: `${border_width}px solid ${isOpen ? border_active_color : border_color}`,
      borderRadius: `${border_radius}px`,
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'space-between' as const,
      alignItems: 'center',
      fontSize: `${text_font_size}px`,
      color: text_color,
      transition: 'all 0.2s ease',
      outline: 'none'
    },
    selectedContent: {
      display: 'flex',
      justifyContent: 'space-between' as const,
      alignItems: 'center',
      width: '100%',
      minHeight: '20px'
    },
    dropdownIcon: {
      width: '24px',
      height: '24px',
      transition: 'transform 0.2s ease',
      flexShrink: 0,
      marginLeft: '12px',
      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
      color: '#878787'
    },
    menu: {
      position: 'absolute' as const,
      top: '100%',
      left: 0,
      right: 0,
      backgroundColor: dropdown_background_color,
      border: `${border_width}px solid ${border_color}`,
      borderTop: 'none',
      borderRadius: `0 0 ${border_radius}px ${border_radius}px`,
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      zIndex: 1000,
      maxHeight: '300px',
      overflowY: 'auto' as const,
      opacity: isOpen ? 1 : 0,
      visibility: isOpen ? ('visible' as const) : ('hidden' as const),
      transform: isOpen ? 'translateY(0)' : 'translateY(-10px)',
      transition: 'all 0.2s ease'
    },
    option: {
      padding: `${option_padding_vertical}px ${padding_horizontal}px`,
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'space-between' as const,
      alignItems: 'center',
      borderBottom: `1px solid ${option_border_color}`,
      transition: 'background-color 0.2s ease'
    },
    optionSelected: {
      backgroundColor: option_selected_color
    },
    optionUnavailable: {
      opacity: 0.5,
      cursor: 'not-allowed'
    },
    optionName: {
      fontSize: `${text_font_size}px`,
      color: text_color,
      fontWeight: '500'
    },
    optionSubtext: {
      fontSize: `${subtext_font_size}px`,
      color: subtext_color,
      marginLeft: '12px'
    },
    selectedSubtext: {
      fontSize: `${subtext_font_size}px`,
      color: subtext_color,
      marginLeft: '8px'
    },
    optionUnavailableText: {
      color: unavailable_color
    },
    hiddenSelect: {
      position: 'absolute' as const,
      opacity: 0,
      pointerEvents: 'none' as const,
      left: '-9999px'
    }
  };

  // Mobile responsive adjustments
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 749;
  if (isMobile) {
    styles.trigger.padding = `${mobile_padding_vertical}px ${mobile_padding_horizontal}px`;
    styles.trigger.fontSize = `${mobile_text_font_size}px`;
    styles.optionName.fontSize = `${mobile_text_font_size}px`;
    styles.optionSubtext.fontSize = `${mobile_subtext_font_size}px`;
    styles.container.maxWidth = '100%';
  }

  if (
    (options.length === 0 && !isLoadingTagProducts) ||
    !options.some((option) => option.id === selectedOption?.id) ||
    (options.length === 1 &&
      (('productHandle' in options[0] && options[0].productHandle === product?.handle) ||
        options[0].id === product?.id))
  )
    return null;

  return (
    <div
      className={`ai-variant-selector-${ai_gen_id}`}
      style={styles.container}
      ref={dropdownRef}
      data-product-id={product?.id}
      data-variant-source={variant_source}
      data-tag-name={tag_name}
      data-tag-action={tag_selection_action}
    >
      {show_label && (
        <label
          style={styles.label}
          htmlFor={`variant-select-${ai_gen_id}`}
        >
          {label_text}
        </label>
      )}

      <div style={styles.dropdown}>
        <button
          type='button'
          style={styles.trigger}
          aria-haspopup='listbox'
          aria-expanded={isOpen}
          aria-labelledby={`variant-select-${ai_gen_id}`}
          onClick={handleToggleDropdown}
          onKeyDown={handleKeyDown}
          className='react-variant-selector-trigger b7--medium'
        >
          <div style={styles.selectedContent}>
            <span>{selectedOption ? selectedOption.title : placeholder_text}</span>
            <span>{selectedOption?.subtext}</span>
            {selectedOption && show_price_subtext && selectedOption.price && !('isTag' in selectedOption) && (
              <span style={styles.selectedSubtext}>{formatPrice(selectedOption.price)}</span>
            )}
            {selectedOption && !selectedOption.available && (
              <span style={{ ...styles.selectedSubtext, ...styles.optionUnavailableText }}>{unavailable_text}</span>
            )}
            {selectedOption && selectedOption.additionalDetails && (
              <span className='b8--medium text-[#878787]'>{selectedOption.additionalDetails}</span>
            )}
          </div>
          <i
            className={`icon icon-chevron-down text-[24px] text-[#878787] ml-3 transition-transform ${isOpen ? 'rotate-180' : 'rotate-0'}`}
          ></i>
        </button>

        <div
          style={styles.menu}
          role='listbox'
        >
          {options &&
            options.length > 0 &&
            options.map((option) => (
              <div
                key={option.id}
                style={{
                  ...styles.option,
                  ...(selectedOption?.id === option.id ? styles.optionSelected : {}),
                  ...(!option.available ? styles.optionUnavailable : {})
                }}
                onClick={() => option.available && handleSelectOption(option)}
                onMouseEnter={(e) => {
                  if (option.available) {
                    (e.target as HTMLElement).style.backgroundColor = option_hover_color;
                  }
                }}
                onMouseLeave={(e) => {
                  if (option.available) {
                    (e.target as HTMLElement).style.backgroundColor =
                      selectedOption?.id === option.id ? option_selected_color : 'transparent';
                  }
                }}
              >
                <span
                  style={{
                    ...styles.optionName,
                    ...(!option.available ? styles.optionUnavailableText : {})
                  }}
                >
                  {option.title}
                </span>
                <span style={styles.selectedSubtext}>{option.subtext}</span>
                {show_price_subtext && option.price && !('isTag' in option) && (
                  <span
                    style={{
                      ...styles.optionSubtext,
                      ...(!option.available ? styles.optionUnavailableText : {})
                    }}
                  >
                    {formatPrice(option.price)}
                  </span>
                )}
                {!option.available && (
                  <span style={{ ...styles.optionSubtext, ...styles.optionUnavailableText }}>{unavailable_text}</span>
                )}
                {/* {'isTag' in option && option.isTag && tag_selection_action === "redirect" && (
                  <span style={{
                    ...styles.optionSubtext,
                    fontSize: `${subtext_font_size - 2}px`,
                    fontStyle: 'italic'
                  }}>
                    (View Product)
                  </span>
                )} */}
                {(option as TagOption).additionalDetails && (
                  <span
                    style={{
                      ...styles.optionSubtext,
                      fontSize: `${subtext_font_size - 2}px`
                    }}
                  >
                    {(option as TagOption).additionalDetails}
                  </span>
                )}
              </div>
            ))}
        </div>
      </div>

      {/* Hidden select for form submission */}
      {product_form_id && (
        <select
          name='id'
          id={`variant-select-${ai_gen_id}`}
          style={styles.hiddenSelect}
          form={product_form_id}
          value={selectedOption?.id || ''}
          onChange={() => {}} // Controlled by React
        >
          {variant_source === 'variants'
            ? // Regular variants
              options.map((option) => (
                <option
                  key={option.id}
                  value={option.id}
                  disabled={!option.available}
                >
                  {option.title} - {option.price ? formatPrice(option.price) : ''}
                </option>
              ))
            : // Tag-based options - create options for all tag products' variants
              tagProducts
                .map((tagProduct) => {
                  const firstVariant = tagProduct.variants?.edges?.[0]?.node;
                  if (!firstVariant) return null;

                  // Convert price from string to cents for formatPrice function
                  const priceInCents = Math.round(parseFloat(firstVariant.price.amount) * 100);

                  return (
                    <option
                      key={firstVariant.id}
                      value={firstVariant.id}
                      disabled={!firstVariant.availableForSale}
                    >
                      {tagProduct.title} - {formatPrice(priceInCents)}
                    </option>
                  );
                })
                .filter(Boolean)}
        </select>
      )}
    </div>
  );
};
